import { defineStore } from 'pinia';

import type { CartItemType } from '~/utils/api/cart';
import type {
  OrderDeliveryDataType,
  OrderInfoType,
  OrderOtherRecipientDataType,
  OrderPaymentDataType,
  OrderSummaryDataType,
} from '~/utils/api/order';
import { cartItemKey } from '~/utils/twoPlusOne';

const RESULT_DATA_VERSION = 1;
const RESULT_DATA_KEY = 'order-result-data';

export const useOrderStore = defineStore('order', () => {
  const t = useNuxtApp().$i18n.t;
  const modalStore = useModalStore();
  const productCartStore = useProductCartStore();
  const router = useRouter();

  const initialized = ref(false);
  const isReady = ref(false);
  const isUserReady = ref(false);
  const localCartData = ref<CartItemType[]>();
  const otherRecipient = ref<OrderOtherRecipientDataType>(null);
  const comment = ref('');
  const delivery = reactive<OrderDeliveryDataType>({
    isReady: false,
    ozonUnavailableItems: [],
    addresses: [],
    variants: [],
    current: null,
  });
  const payment = reactive<OrderPaymentDataType>({
    isReady: false,
    current: null,
    variants: [],
  });
  const summary = reactive<OrderSummaryDataType>({
    basketPrice: 0,
    deliveryPrice: 0,
    discountPrice: 0,
    total: 0,
  });
  const blockKeys = reactive<Record<string, boolean>>({});
  const result = reactive({
    initialized: false,
    message: '',
    orderId: 0,
    isOzon: false,
  });

  const cartData = computed(() => localCartData.value || productCartStore.cartData.filter((el) => el.isAvailable));
  const findCurrentCartItem = (items: CartItemType[], item: CartItemType) =>
    items.find((current) => cartItemKey(current) === cartItemKey(item));

  const updateOrderState = (value: OrderInfoType) => {
    localCartData.value = value.basket.items;
    otherRecipient.value = value.user.otherReceiver;
    isUserReady.value = value.user.isReady;
    isReady.value = value.isReady;
    Object.assign(delivery, value.delivery);
    Object.assign(payment, value.payment);
    Object.assign(summary, value.summary);
  };

  const updateCartItem = (item: CartItemType, productItem: ProductType | undefined, quantity: number) => {
    const source = cartData.value;
    const currentItem = findCurrentCartItem(source, item);
    if (!currentItem) return;

    // Split 2+1: чекаут показывает один товар несколькими строками (winner за 1 ₽ —
    // отдельной строкой), пользователь правит одну строку. Итог по sku = счётчик
    // строки + остальные строки этого товара; 0 = удалить весь товар (без агрегации).
    const othersQty =
      quantity > 0
        ? source
            .filter((el) => el.skuId === currentItem.skuId && el !== currentItem)
            .reduce((acc, el) => acc + el.quantity, 0)
        : 0;
    localCartData.value = source.map((el) => el === currentItem ? { ...el, quantity } : el);

    return productCartStore.updateCartItem({ ...currentItem, quantity: quantity + othersQty }, productItem);
  };

  const deleteCartItem = (item: CartItemType, productItem?: ProductType) => {
    // Split 2+1: удаляем одну строку, а не весь товар — остальные строки этого sku
    // живут (убрали winner за 1 ₽ — полные единицы остаются). Итог по sku = количество
    // остальных строк: >0 → PUT оставшегося, 0 → прежний DELETE всего товара.
    // Пока открыто подтверждение, сервер может заменить объекты строк.
    // Находим выбранную строку по тому же ключу, который использует список.
    const source = cartData.value;
    const currentItem = findCurrentCartItem(source, item);
    if (!currentItem) return;

    const othersQty = source
      .filter((el) => el.skuId === currentItem.skuId && el !== currentItem)
      .reduce((acc, el) => acc + el.quantity, 0);
    localCartData.value = source.filter((el) => el !== currentItem);

    return productCartStore.updateCartItem({ ...currentItem, quantity: othersQty }, productItem);
  };

  const fetchCurrentOrder = async () => {
    const requestRevision = productCartStore.getMutationRevision();
    const isCurrent = () => requestRevision === productCartStore.getMutationRevision();
    const data = await OrderAPI.current(null, {
      onError: (e) => {
        if (!isCurrent()) return;
        if (e.context?.response?.status === 403) {
          modalStore.showInfo({
            title: t('store.order.message.empty_error'),
            action: t('store.order.message.start'),
          });
          router.push('/catalog');
        } else {
          logError(e, 'Ошибка в оформлении заказа');
          modalStore.showError({
            title: getFetchErrorMessage(e, t('store.order.message.form_error')),
            action: t('store.order.message.action'),
          });
        }
      },
    });
    if (!isCurrent()) return;
    updateOrderState(data);

    nextTick(() => {
      initialized.value = true;
    });
  };

  const initializeSuccessfulData = () => {
    if (!result.initialized && typeof localStorage !== 'undefined' && localStorage[RESULT_DATA_KEY]) {
      const data = JSON.parse(localStorage[RESULT_DATA_KEY]);
      if (data.version === RESULT_DATA_VERSION) {
        result.initialized = true;
        result.orderId = data.orderId;
        result.message = data.message;
        result.isOzon = data.isOzon ?? false;
      }
    }
  };

  const prepareSuccessfulData = (orderId: number, message: string, isOzon = false) => {
    result.initialized = true;
    result.orderId = orderId;
    result.message = message;
    result.isOzon = isOzon;
  };

  const setBlockKey = (key: string) => (blockKeys[key] = true);

  const deleteBlockKey = (key: string) => delete blockKeys[key];

  const clear = () => {
    localCartData.value = undefined;
    comment.value = '';
  };

  const clearCartData = () => {
    clear();
    productCartStore.clearCartData();
  };

  return {
    initialized,
    isReady,
    isUserReady,
    cartData,
    otherRecipient,
    comment,
    delivery,
    payment,
    summary,
    blockKeys,
    updateOrderState,
    fetchCurrentOrder,
    updateCartItem,
    deleteCartItem,
    clearCartData,
    clear,

    setBlockKey,
    deleteBlockKey,

    result,
    initializeSuccessfulData,
    prepareSuccessfulData,
  };
});
